import { z } from "zod";
/**
 * A row can legitimately need more than one prune when unknown keys are nested
 * inside a container that itself only validates once its parent does. The
 * ceiling exists so a pathological row terminates as malformed rather than
 * spinning; it is never reached by a row that merely grew fields.
 */
const MAX_PRUNE_ROUNDS = 16;
const issuePath = (path) => path.join(".");
function describeIssue(issue) {
    return { path: issuePath(issue.path), code: issue.code, message: issue.message };
}
/**
 * Remove exactly the keys the schema named, at exactly the container it named
 * them in. Containers are copied rather than mutated so the caller's parsed
 * JSON is never altered underneath it, and nothing is ever added.
 */
function pruneUnknownKeys(value, path, keys) {
    if (value === null || typeof value !== "object")
        return value;
    if (path.length === 0) {
        if (Array.isArray(value))
            return value;
        const next = { ...value };
        for (const key of keys)
            delete next[key];
        return next;
    }
    const [head, ...rest] = path;
    if (Array.isArray(value)) {
        const index = typeof head === "number" ? head : Number(head);
        if (!Number.isInteger(index) || index < 0 || index >= value.length)
            return value;
        const next = value.slice();
        next[index] = pruneUnknownKeys(value[index], rest, keys);
        return next;
    }
    const container = value;
    const key = String(head);
    if (!Object.prototype.hasOwnProperty.call(container, key))
        return value;
    return { ...container, [key]: pruneUnknownKeys(container[key], rest, keys) };
}
/**
 * Read one stored row through `schema`, tolerating forward-compatible drift
 * and reporting anything else as a problem on that row.
 *
 * `schema` is the unmodified write schema. It is never relaxed, and this
 * function is never a write path.
 */
export function readStoredProjection(schema, objectId, stored) {
    let candidate = stored;
    const drift = [];
    for (let round = 0; round < MAX_PRUNE_ROUNDS; round += 1) {
        const parsed = schema.safeParse(candidate);
        if (parsed.success)
            return { readable: true, objectId, value: parsed.data, drift };
        const issues = parsed.error.issues;
        const unknownKeyIssues = issues.filter((issue) => issue.code === "unrecognized_keys");
        // Any issue that is not an unknown key is a statement about content this
        // build DOES define, so the row is malformed and says so. Reporting every
        // issue — not just the ones that blocked the prune — keeps the row's whole
        // story on the row.
        if (unknownKeyIssues.length === 0 || unknownKeyIssues.length !== issues.length) {
            return { readable: false, objectId, problems: issues.map(describeIssue) };
        }
        for (const issue of unknownKeyIssues) {
            drift.push({ path: issuePath(issue.path), keys: [...issue.keys] });
            candidate = pruneUnknownKeys(candidate, issue.path, issue.keys);
        }
    }
    return {
        readable: false,
        objectId,
        problems: [
            {
                path: "",
                code: "unrecognized_keys",
                message: `stored record still reported unknown keys after ${MAX_PRUNE_ROUNDS} prune rounds`,
            },
        ],
    };
}
/**
 * Raised when a row addressed BY IDENTITY cannot be read. A caller asking for
 * one specific row has no sibling to protect, so the honest answer is this
 * error naming the row and the reason — never `null`, which would report a row
 * that exists as a row that does not.
 */
export class StoredProjectionReadError extends Error {
    objectId;
    problems;
    constructor(objectId, problems) {
        super(`stored record ${objectId} does not match this build's protocol: ` +
            problems.map((p) => `${p.path || "<root>"}: ${p.message}`).join("; "));
        this.name = "StoredProjectionReadError";
        this.objectId = objectId;
        this.problems = problems;
    }
}
/**
 * Read a set of stored rows. One undecodable sibling reduces the list by that
 * one row and reports it; it never refuses the other rows, and it never
 * disappears.
 */
export function readStoredProjectionList(schema, rows) {
    const records = [];
    const unreadable = [];
    const drift = [];
    for (const row of rows) {
        const read = readStoredProjection(schema, row.objectId, row.stored);
        if (!read.readable) {
            unreadable.push({ objectId: read.objectId, problems: read.problems });
            continue;
        }
        records.push(read.value);
        if (read.drift.length > 0)
            drift.push({ objectId: read.objectId, drift: read.drift });
    }
    return { records, unreadable, drift };
}
/**
 * What a tenant's storage says about itself when read by THIS build: the rows
 * it could not decode, and the readable rows that carried fields it does not
 * define. Reported beside the records so a row this build cannot read is never
 * merely missing from a list.
 */
export const storedProjectionProblemSchema = z
    .object({
    path: z.string(),
    code: z.string().min(1),
    message: z.string().min(1),
})
    .strict();
export const universalStorageReadIssuesSchema = z
    .object({
    unreadable: z
        .array(z
        .object({
        kind: z.string().min(1),
        objectId: z.string().min(1),
        problems: z.array(storedProjectionProblemSchema).min(1),
    })
        .strict())
        .describe("Rows this tenant stores that this build could not decode. They are absent from the record lists above and named here, so a row that did not parse is never mistaken for a row that does not exist."),
    forwardCompatibleDrift: z
        .array(z
        .object({
        kind: z.string().min(1),
        objectId: z.string().min(1),
        unknownKeys: z.array(z.string().min(1)).min(1),
    })
        .strict())
        .describe("Readable rows carrying fields this build does not define. They are returned in full; only the undefined fields are absent."),
})
    .strict();
/**
 * Read one stored row addressed by identity. Tolerates forward-compatible
 * drift exactly as the list read does, and throws {@link StoredProjectionReadError}
 * — naming the row — when the row is genuinely malformed.
 */
export function requireStoredProjection(schema, objectId, stored) {
    const read = readStoredProjection(schema, objectId, stored);
    if (!read.readable)
        throw new StoredProjectionReadError(read.objectId, read.problems);
    return read.value;
}
